import { Heading, Link, Text } from "@ttc3k/trekker";
import { format } from "date-fns";
import { EnumLeadSource, LeadFragmentFragment } from "gql/generated";
import { useTranslation } from "react-i18next";
import { Shimmer } from "shared/components/atoms";
import { CopyToClipboard } from "shared/components/molecules";
import { DATE_PATTERN_STANDARD, formatPhoneNumber } from "shared/utils";
import { getMultilingualString } from "shared/utils/getMultilingualString";
import { Flex } from "styled-system/jsx";

type LeadModalHeaderProps = {
  lead: LeadFragmentFragment | null | undefined;
  onOpenBroadcastFromLead?: (broadcastId: string, resourceId: string) => void;
};

export const LeadModalHeader = ({
  lead,
  onOpenBroadcastFromLead,
}: LeadModalHeaderProps) => {
  const { t } = useTranslation("people", { keyPrefix: "LEAD_MODAL" });

  const sentToName =
    getMultilingualString(lead?.metadata.booker?.name ?? {}) ||
    getMultilingualString(
      lead?.metadata.booker?.bundles?.[0]?.product?.name ?? {},
    ) ||
    getMultilingualString(lead?.metadata.storefront?.name ?? {}) ||
    getMultilingualString(lead?.metadata.operator?.name ?? {}) ||
    getMultilingualString(lead?.metadata.organization?.name ?? {});

  const subject = lead?.subject || sentToName;
  const sentFrom = t("SENT_FROM", {
    sentFrom: `${lead?.person?.firstName} ${lead?.person?.lastName}`,
  });

  return (
    <Flex
      flexDir="column"
      gap="300"
      p="400"
      bgColor="white"
      borderRadius="150"
      boxShadow="0 10px 15px -5px rgba(10, 10, 10, 0.10), 0 5px 5px -2px rgba(10, 10, 10, 0.05)"
    >
      {lead ? (
        <Heading size="h4">{subject}</Heading>
      ) : (
        <Shimmer h="36px" w="full" />
      )}
      <Flex flexDir="column" gap="100">
        {lead ? (
          <Flex flexDir="column" gap="0">
            <Text
              visual="bodyMedium"
              color="text.mid"
              lineClamp={1}
              wordBreak="break-all"
            >
              {sentFrom}
            </Text>
            {lead.person?.email && (
              <CopyToClipboard
                text={lead?.person?.email}
                visual="bodyMedium"
                color="text.mid"
                opacity={1}
                buttonProps={{
                  width: "fit-content",
                }}
              />
            )}
            {lead.person?.phone && (
              <CopyToClipboard
                text={formatPhoneNumber(lead?.person?.phone)}
                visual="bodyMedium"
                color="text.mid"
                opacity={1}
                buttonProps={{
                  width: "fit-content",
                }}
              />
            )}
            {lead.link && (
              <Link
                href={lead.link}
                target="_blank"
                rel="noopener noreferrer"
                width="fit-content"
                color="text.mid"
                _hover={{
                  textDecoration: "underline",
                  color: "text.dark",
                }}
                _focusVisible={{
                  textDecoration: "underline",
                  color: "text.dark",
                }}
              >
                {lead.link}
              </Link>
            )}
            {lead.source === EnumLeadSource.Broadcast &&
              lead.broadcastId &&
              onOpenBroadcastFromLead && (
                <Flex flexDir="row" gap="100" alignItems="baseline" flexWrap="wrap">
                  <Text visual="bodyMedium" color="text.mid">
                    {t("SOURCE_PREFIX")}
                  </Text>
                  <Text
                    as="button"
                    type="button"
                    visual="bodyMedium"
                    color="text.accent.blue.mid"
                    width="fit-content"
                    textAlign="left"
                    _hover={{
                      textDecoration: "underline",
                      color: "text.accent.blue.dark",
                    }}
                    _focusVisible={{
                      textDecoration: "underline",
                      color: "text.accent.blue.dark",
                    }}
                    cursor="pointer"
                    onClick={() =>
                      onOpenBroadcastFromLead(
                        String(lead.broadcastId),
                        lead.sourceDetails ?? "",
                      )
                    }
                  >
                    {t("BROADCAST_LINK")}
                  </Text>
                </Flex>
              )}
            <Text visual="bodyMedium" color="text.mid">
              {format(lead?.createdDateISO ?? "", DATE_PATTERN_STANDARD)}
            </Text>
          </Flex>
        ) : (
          <Shimmer h="72px" w="full" />
        )}
      </Flex>
    </Flex>
  );
};
